import React, { useEffect, useState } from 'react';
import { useAuth } from '../hooks/useAuth';
import './UserManagement.css';

export default function UserManagement() {
  const { token } = useAuth();
  const [users, setUsers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showForm, setShowForm] = useState(false);
  const [formData, setFormData] = useState({ username: '', password: '', email: '', is_admin: false });
  const [error, setError] = useState('');
  const [success, setSuccess] = useState('');
  const [editingUser, setEditingUser] = useState(null);
  const [editFormData, setEditFormData] = useState({ username: '', email: '', is_admin: false });

  useEffect(() => {
    fetchUsers();
  }, []);

  const fetchUsers = async () => {
    try {
      const response = await fetch('/.netlify/functions/admin-users', {
        headers: { Authorization: `Bearer ${token}` }
      });
      if (response.ok) {
        const data = await response.json();
        setUsers(data);
      }
    } catch (err) {
      setError('Failed to fetch users');
    } finally {
      setLoading(false);
    }
  };

  const handleAddUser = async (e) => {
    e.preventDefault();
    setError('');
    setSuccess('');

    try {
      const response = await fetch('/.netlify/functions/admin-users', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(formData)
      });

      if (response.ok) {
        setSuccess('User added successfully');
        setFormData({ username: '', password: '', email: '', is_admin: false });
        setShowForm(false);
        fetchUsers();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to add user');
      }
    } catch (err) {
      setError('Error adding user');
    }
  };

  const handleDeleteUser = async (id) => {
    if (!window.confirm('Are you sure you want to delete this user?')) return;

    try {
      const response = await fetch(`/.netlify/functions/admin-users?id=${id}`, {
        method: 'DELETE',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('User deleted');
        fetchUsers();
      } else {
        setError('Failed to delete user');
      }
    } catch (err) {
      setError('Error deleting user');
    }
  };

  const handleDisableUser = async (id) => {
    try {
      const response = await fetch(`/.netlify/functions/admin-users?id=${id}&action=disable`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('User disabled');
        fetchUsers();
      } else {
        setError('Failed to disable user');
      }
    } catch (err) {
      setError('Error disabling user');
    }
  };

  const handleActivateUser = async (id) => {
    try {
      const response = await fetch(`/.netlify/functions/admin-users?id=${id}&action=enable`, {
        method: 'PUT',
        headers: { Authorization: `Bearer ${token}` }
      });

      if (response.ok) {
        setSuccess('User activated');
        fetchUsers();
      } else {
        setError('Failed to activate user');
      }
    } catch (err) {
      setError('Error activating user');
    }
  };

  const handleEditUser = (user) => {
    setEditingUser(user);
    setEditFormData({
      username: user.username,
      email: user.email || '',
      is_admin: user.is_admin || false
    });
    setError('');
    setSuccess('');
  };

  const handleCancelEdit = () => {
    setEditingUser(null);
  };

  const handleSaveUser = async (e) => {
    e.preventDefault();
    if (!editingUser) return;

    try {
      const response = await fetch(`/.netlify/functions/admin-users?id=${editingUser.id}&action=update`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
          Authorization: `Bearer ${token}`
        },
        body: JSON.stringify(editFormData)
      });

      if (response.ok) {
        setSuccess('User updated successfully');
        setEditingUser(null);
        fetchUsers();
      } else {
        const err = await response.json();
        setError(err.error || 'Failed to update user');
      }
    } catch (err) {
      setError('Error updating user');
    }
  };

  if (loading) return <div>Loading users...</div>;

  return (
    <div className="user-management">
      <h2>User Management</h2>

      {error && <div className="alert alert-error">{error}</div>}
      {success && <div className="alert alert-success">{success}</div>}

      <button className="btn btn-primary" onClick={() => setShowForm(!showForm)}>
        {showForm ? 'Cancel' : 'Add New User'}
      </button>

      {showForm && (
        <form className="add-user-form" onSubmit={handleAddUser}>
          <input
            type="text"
            placeholder="Username"
            value={formData.username}
            onChange={(e) => setFormData({ ...formData, username: e.target.value })}
            required
          />
          <input
            type="password"
            placeholder="Password"
            value={formData.password}
            onChange={(e) => setFormData({ ...formData, password: e.target.value })}
            required
          />
          <input
            type="email"
            placeholder="Email (optional)"
            value={formData.email}
            onChange={(e) => setFormData({ ...formData, email: e.target.value })}
          />
          <label>
            <input
              type="checkbox"
              checked={formData.is_admin}
              onChange={(e) => setFormData({ ...formData, is_admin: e.target.checked })}
            />
            Admin user
          </label>
          <button type="submit" className="btn btn-success">
            Add User
          </button>
        </form>
      )}

      {editingUser && (
        <form className="edit-user-form" onSubmit={handleSaveUser}>
          <h3>Edit user: {editingUser.username}</h3>
          <input
            type="text"
            placeholder="Username"
            value={editFormData.username}
            onChange={(e) => setEditFormData({ ...editFormData, username: e.target.value })}
            required
          />
          <input
            type="email"
            placeholder="Email"
            value={editFormData.email}
            onChange={(e) => setEditFormData({ ...editFormData, email: e.target.value })}
            required
          />
          <label>
            <input
              type="checkbox"
              checked={editFormData.is_admin}
              onChange={(e) => setEditFormData({ ...editFormData, is_admin: e.target.checked })}
            />
            Admin user
          </label>
          <div className="edit-buttons">
            <button type="submit" className="btn btn-success">
              Save Changes
            </button>
            <button type="button" className="btn btn-warning" onClick={handleCancelEdit}>
              Cancel
            </button>
          </div>
        </form>
      )}

      <div className="users-table">
        <table>
          <thead>
            <tr>
              <th>Username</th>
              <th>Email</th>
              <th>Admin</th>
              <th>Status</th>
              <th>Created</th>
              <th>Actions</th>
            </tr>
          </thead>
          <tbody>
            {users.map((user) => (
              <tr key={user.id}>
                <td>{user.username}</td>
                <td>{user.email || '-'}</td>
                <td>{user.is_admin ? 'Yes' : 'No'}</td>
                <td>{user.is_active ? 'Active' : 'Disabled'}</td>
                <td>{new Date(user.created_at).toLocaleDateString()}</td>
                <td className="actions">
                  {user.is_active ? (
                    <button className="btn btn-warning" onClick={() => handleDisableUser(user.id)}>
                      Deactivate
                    </button>
                  ) : (
                    <button className="btn btn-success" onClick={() => handleActivateUser(user.id)}>
                      Activate
                    </button>
                  )}
                  <button className="btn btn-info" onClick={() => handleEditUser(user)}>
                    Edit
                  </button>
                  <button className="btn btn-danger" onClick={() => handleDeleteUser(user.id)}>
                    Delete
                  </button>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </div>
  );
}
